import { getClinicUserQuery } from 'shared/hooks/useClinicUser';
import {
  ClinicPimsIntegrationType,
  useRequestBitwerxIntegrationMutation,
  VetDataIntegrationSystem,
} from 'shared/types/graphql';
import {
  getBitwerxIntegration,
  hasInitiatedBitwerxIntegration,
  needsBitwerxIntegration,
} from '../utils/getBitwerxIntegrationData';
import { useIntegrationsProvider } from '../context';

interface RequestBitwerxArgs {
  clinicContactEmail: string;
  forPayments: boolean;
  callback?: () => void;
}

// TODO: This should all be migrated to the provider
export const useBitwerxData = (): {
  invoiceReadCapable: boolean;
  writebackCapable: boolean;
  bitwerxIntegrationId: string | undefined;
  needsBitwerxForServiceReminders: boolean;
  needsBitwerxForPaymentWritebacks: boolean;
  bitwerxPending: boolean;
  hasBitwerx: boolean;
  bitwerxLoading: boolean;
  requestBitwerx: (args: RequestBitwerxArgs) => Promise<void>;
} => {
  const { allIntegrations, allActiveIntegrations } = useIntegrationsProvider();

  const invoiceReadSystems: (VetDataIntegrationSystem | undefined)[] = [
    VetDataIntegrationSystem.Cornerstone,
    VetDataIntegrationSystem.Impromed,
    VetDataIntegrationSystem.Avimark,
  ];
  const invoiceReadCapable = !!allIntegrations?.some(
    (integration) => integration.isActive && invoiceReadSystems.includes(integration.vetdata?.system),
  );

  const invoiceWritebackSystems: (VetDataIntegrationSystem | undefined)[] = [
    VetDataIntegrationSystem.Cornerstone,
    VetDataIntegrationSystem.Avimark,
  ];

  const writebackCapable = !!allActiveIntegrations?.some((integration) =>
    invoiceWritebackSystems.includes(integration.vetdata?.system),
  );

  const bitwerxIntegrationId = allIntegrations?.find(
    (integration) => integration.type === ClinicPimsIntegrationType.Bitwerx,
  )?.id;

  const needsBitwerxForServiceReminders = needsBitwerxIntegration(allIntegrations, 'ServiceReminders');

  const needsBitwerxForPaymentWritebacks = needsBitwerxIntegration(allIntegrations, 'PaymentWritebacks');

  const bitwerxPending = hasInitiatedBitwerxIntegration(allIntegrations);

  const bitwerxIntegration = getBitwerxIntegration(allIntegrations);
  const hasBitwerx = !!bitwerxIntegration?.isActive && !!bitwerxIntegration?.bitwerx?.practiceId;

  const [requestBitwerxIntegration, { loading: bitwerxLoading }] = useRequestBitwerxIntegrationMutation({
    refetchQueries: [getClinicUserQuery],
  });

  const requestBitwerx = async ({ clinicContactEmail, forPayments, callback }: RequestBitwerxArgs): Promise<void> => {
    await requestBitwerxIntegration({ variables: { data: { clinicContactEmail, forPayments } } });
    if (callback) callback();
  };

  return {
    invoiceReadCapable,
    writebackCapable,
    bitwerxIntegrationId,
    needsBitwerxForServiceReminders,
    needsBitwerxForPaymentWritebacks,
    bitwerxPending,
    hasBitwerx,
    requestBitwerx,
    bitwerxLoading,
  };
};
